import { BREAKPOINTS } from "../config/breakpoints";
import React from "react";
import styled, { css } from "styled-components";
import { zIndex } from "../config/zIndex";

const StyledTable = styled.table`
	position: relative;
	margin: ${(props) => props.margin};
	border-radius: 0.625rem;
	border-collapse: seperate;
	border-spacing: 0 0.625rem;
	width: ${(props) => props.width};
	table-layout: ${(props) => props.tableLayout || "auto"};
	font-family: ${(props) => props.fontFamily};
	@media (max-width: ${BREAKPOINTS.desktop}px) {
		width: ${(props) => props.widthBefore1000};
	}
`;

const TableBody = styled.tbody`
	width: 100%;
	overflow-y: auto;
	height: 100%;
`;

const Tablehead = styled.th`
	width: ${(props) => props.width};
	font-size: ${(props) => props.fontSize};
	font-weight: 600;
	letter-spacing: 0.1em;
	text-transform: uppercase;
	text-align: ${(props) => props.textAlign};
	padding: ${(props) => props.padding};
	width: ${(props) => props.width};
	border-radius: ${(props) => props.borderRadius};
	/*
	 * Column labels wrap rather than truncate. With nowrap + ellipsis a
	 * 6-column fixed table clips "TASK GROUP ID" to "TASK GROUP I…" above
	 * 1300px, which is what pushed call sites into overriding fontSize and
	 * padding by hand. A two-line header costs ~18px and reads correctly.
	 */
	white-space: normal;
	overflow-wrap: break-word;
	@media (max-width: 800px) {
		letter-spacing: 0;
		font-size: 1rem;
	}
`;

const Tablerow = styled.tr`
	position:${(props) => (props.head ? "sticky" : "relative")};
	top:${(props) => (props.head ? props.top : 0)};
	width:100%;
	background:${(props) => (props.head ? "#f2f2f2" : props.background)};
	border:${(props) => props.border};
	box-shadow:${(props) => props.boxShadow};
	transition:0.1s all ease-in-out;
	z-index:${(props) => (props.head ? zIndex.tableStickyHeader : "")};
	&:hover {
		${'' /* box-shadow:${(props) =>
				props.head || !props.hoverEffect
					? ""
					: "rgba(0, 0, 0, 0.24) 0px 3px 8px;"} */}
		transform:${(props) => props.head || !props.hoverEffect ? "" : "scale(1.01)"}
	}
	@media(max-width:${(props) => props.breakpoint}) {
		display:${(props) => (props.head ? "none" : "block")};
		margin:0 0 1rem 0;
		border-radius:0.625rem;
		width:100%:
	}
	${props => props.clickable && css`
		cursor: pointer;
	`};
	${props => props.disabled && css`
		opacity: 0.5;
		pointer-events:none;
	`};
`;

const Tablecolumn = styled.td`
	width: ${(props) => props.width};
	line-height: 1.5625rem;
	text-align: ${(props) => props.textAlign};
	border-radius: ${(props) => props.borderRadius};
	padding: ${(props) => (props.head ? "" : props.padding)};
	font-size: ${(props) => props.fontSize};
	overflow-wrap: anywhere;
	@media (max-width: ${(props) => props.breakpoint}) {
		display: inline-block;
		max-width: 100%;
		width: 100%;
		text-align: left;
		padding: 1rem 1rem 1rem 50%;
		box-sizing: border-box;
		position: relative;
		overflow: hidden;
		white-space: normal;
		overflow-wrap: anywhere;
		font-size: 0.8rem;
		&::before {
			content: attr(title);
			left: 0;
			width: max-content;
			position: absolute;
			padding-left: 0.9375rem;
			font-size: 0.8125rem;
			font-weight: bold;
		}
	}
	@media (max-width: ${BREAKPOINTS.wide}px) and (min-width:${props => props.breakpoint}) {
		padding:1rem 0.5rem;
	}
	&:first-child {
		border-radius: 10px 0 0 10px;
	}
	&:last-child {
		border-radius: 0 10px 10px 0;
	}
	${(props) =>
		props.ellipsis &&
		css`
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		`}
`;

StyledTable.defaultProps = {
	fontFamily: "sans-serif",
	width: "100%",
};

Tablerow.defaultProps = {
	background: "#fff",
	hoverEffect: true,
	top: 0,
	// Width at which this table switches from a normal table to the
	// stacked mobile card layout. Has a default so call sites don't
	// need to set it explicitly.
	breakpoint: "800px",
};

Tablehead.defaultProps = {
	padding: "1rem 0.5rem",
	fontSize: "0.8125rem",
	textAlign: "center",
};

Tablecolumn.defaultProps = {
	padding: "1.5rem",
	fontSize: "1rem",
	width: "max-content",
	textAlign: "center",
	breakpoint: "800px",
};

/**
 * Optional `columns` prop: an array of unitless ratios, one per column, e.g.
 *
 *   <Table tableLayout="fixed" columns={[1.5, 1.7, 1.1, 0.8]}>
 *
 * They are normalised to percentages and emitted as a <colgroup>, so ratios
 * do not have to add up to anything and adding a column does not mean
 * rebalancing every other number. Only meaningful with tableLayout="fixed";
 * omit it entirely when equal columns are fine.
 */
export const Table = (props) => {
	const { innerRef, tableLayout, columns, children, ...rest } = props;
	const total = columns?.length ? columns.reduce((sum, ratio) => sum + ratio, 0) : 0;

	return (
		<StyledTable ref={innerRef} tableLayout={tableLayout} {...rest}>
			{total > 0 ? (
				<colgroup>
					{columns.map((ratio, index) => (
						<col key={index} style={{ width: `${(ratio / total) * 100}%` }} />
					))}
				</colgroup>
			) : null}
			{children}
		</StyledTable>
	);
};

export const Tbody = (props) => {
	return <TableBody {...props} />;
};

export const Thead = (props) => {
	return <Tablehead {...props} />;
};

export const Trow = (props) => {
	return <Tablerow ref={props.innerRef} {...props} />;
};

export const Tcol = (props) => {
	return <Tablecolumn {...props} />;
};
